import * as React from "react";

import * as Redux from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
  Field,
  FieldSet,
  FieldSets,
  Merchant,
  MerchantCollections,
  MerchantFieldSetValueMap,
  ValueOf,
} from "client/lib/apt-merchant";
import { PhoneField } from "client/lib/apt-merchant/src/fields";
import { BankFieldSetNames } from "client/lib/apt-merchant/src/fieldsets";
import * as FormService from "client/services/form";
import { BankVerificationSource } from "client/services/form";
import * as UserService from "client/services/user";
import {
  errorActions,
  FieldErrorMessageMap,
  merchantActions,
  RootState,
} from "client/store";
import { RestoreResponse } from "client/types";
import * as merchantTransforms from "client/utils/merchantTransforms";
import { createMerchantFromMerchantFieldMap } from "client/utils/transforms";

interface UseMerchantModel {
  addFieldSetToCollection: (collectionType: keyof MerchantCollections) => void;
  errors: FieldErrorMessageMap;
  formatMerchantForSave: () => { data: any };
  formatMerchantForSignup: () => any;
  formatMerchantForStoreInfo: () => {
    data: UserService.UpdateStoreInfoRequest;
  };
  formatMerchantForBankInfo: (args: {
    verificationSource: BankVerificationSource;
    fieldSetName: BankFieldSetNames;
    setBankFormComplete: boolean;
  }) => { data: FormService.SubmitBankInfoRequest };
  formatMerchantForCustomerSupportInfo: () => {
    data: UserService.SubmitCustomerSupportInfoRequest;
  };
  formatMerchantForCashPayInfo: () => {
    data: UserService.SubmitCashPayInfoRequest;
  };
  formatMerchantForContactUsInfo: (
    contentBody: string,
    merchantEmail: string,
    file?: File
  ) => UserService.CompleteContactUsFormParams;
  updatePhoneCountryValidationProperty: (
    field: PhoneField,
    value: string
  ) => void;
  getFieldSetUpdater: (
    fieldSet: FieldSet<any>
  ) => (field: Field<any>, value: ValueOf<typeof field>) => void;
  merchant: Merchant;
  removeFieldSetFromCollection: (
    collectionType: keyof MerchantCollections,
    index?: number
  ) => void;
  resetFieldSetToCache: (
    fieldSet: FieldSets.BusinessOwner | FieldSets.StoreLocation
  ) => void;
  setCachedFieldSetValues: (
    values: FieldSets.BusinessOwnerValueMap | FieldSets.StoreLocationValueMap
  ) => void;
  primaryBankVerificationSource: BankVerificationSource;
  setPrimaryBankVerificationSource: (source: BankVerificationSource) => void;
  setMerchant: (data: RestoreResponse) => void;
  setMerchantLocationsFromJson: (
    locations: MerchantFieldSetValueMap["locations"]
  ) => void;
  setOnboardingLink: (link: string) => void;
  syncStoreWithMerchant: () => void;
  validateMerchant: (fieldSet?: FieldSet<any>) => boolean;
  validateCustomFields: (
    fieldProps: string[],
    fieldSet: FieldSets.LeadFieldSet
  ) => boolean;
}

const conditionallyUpdateSalesChannel = (merchantModel: Merchant): boolean => {
  let shouldSyncStoreWithModel = false;
  const isOmniChannel =
    merchantModel.lead.salesChannel.value === "Online and In-store";
  // check to acquirer/platform for square and update sales channel accordingly
  if (
    merchantModel.lead.registeredCountry.value &&
    ["United States", "Australia"].includes(
      merchantModel.lead.registeredCountry.value
    ) &&
    isOmniChannel
  ) {
    // if platform is Square and Acquirer is not Square, set salesChannel value to in-store
    const platform = merchantModel.lead.fieldMap.platform.value;
    const acquirer = merchantModel.lead.fieldMap.acquirer.value;
    if (platform === "Square" && acquirer !== "Square") {
      merchantModel.lead.salesChannel.value = "In-store Only";
      merchantModel.storeDetails.salesChannel.value = "In-store Only";
      shouldSyncStoreWithModel = true;
    } else if (platform !== "Square" && acquirer === "Square") {
      // if platform is NOT Square and Acquirer is Square, set salesChannel value to Online Only
      merchantModel.lead.salesChannel.value = "Online Only";
      merchantModel.storeDetails.salesChannel.value = "Online Only";
      shouldSyncStoreWithModel = true;
    }
  }
  return shouldSyncStoreWithModel;
};

export const copyFromLeadform = (merchant: Merchant): void => {
  const {
    acquirer,
    contactlessPayment,
    firstName,
    industry,
    lastName,
    numOfStores,
    platform,
    registeredCountry,
    salesChannel,
    storeName,
    subIndustry,
    totalAnnualSales,
    totalAnnualSalesInStore,
    website,
  } = merchant.lead;
  merchant.owners[0].firstName.value = firstName.value;
  merchant.owners[0].lastName.value = lastName.value;

  merchant.companyInformation.registeredCountry.value = registeredCountry.value;
  merchant.storeDetails.acquirer.value = acquirer.value;
  merchant.storeDetails.contactlessPayment.value = contactlessPayment.value;
  merchant.storeDetails.industry.value = industry.value;
  merchant.storeDetails.numOfStores.value = numOfStores.value;
  merchant.storeDetails.platform.value = platform.value;
  merchant.storeDetails.salesChannel.value = salesChannel.value;
  merchant.storeDetails.storeName.value = storeName.value;
  merchant.storeDetails.subIndustry.value = subIndustry.value;
  merchant.storeDetails.totalAnnualSales.value = totalAnnualSales.value;
  merchant.storeDetails.totalAnnualSalesInStore.value =
    totalAnnualSalesInStore.value;
  merchant.storeDetails.website.value = website.value;
};

function isRestore(
  response: MerchantFieldSetValueMap | RestoreResponse
): response is RestoreResponse {
  return response?.hasOwnProperty("restore");
}

/**
 * This custom hook should be the only interface used to interact with the
 * Merchant model. We are essentially using RTK / immer as the backing "store"
 * for our merchant model, which enables "observability" for the UI.
 *
 * We may want to eventually move this + merchantSlice into apt-merchant
 * or simply expose our Merchant class to immer directly
 */
export function useMerchantModel(): UseMerchantModel {
  const dispatch = Redux.useDispatch();
  const {
    merchant: { fieldValueMap, merchantAccount, storeLocationsFile },
    userState: { email },
    error: { fieldErrorMap },
  } = Redux.useSelector(({ merchant, userState, error }: RootState) => ({
    merchant,
    userState,
    error,
  }));

  const [cachedFieldSetValues, setCachedFieldSetValues] = React.useState<
    FieldSets.BusinessOwnerValueMap | FieldSets.StoreLocationValueMap
  >();

  const [merchantModel, setMerchantModel] = React.useState(
    createMerchantFromMerchantFieldMap(fieldValueMap)
  );

  /**
   * Since RTK uses immer, changes to the merchant store will always be a new
   * immutable instance.
   *
   * As a result, we can use this React.useEffect hook to update our merchant model
   * whenever the backing store changes.
   */
  React.useEffect(() => {
    setMerchantModel(createMerchantFromMerchantFieldMap(fieldValueMap));
  }, [fieldValueMap]);

  React.useEffect(() => {
    merchantModel.merchantAccount = merchantAccount;
  }, [merchantAccount, merchantModel]);

  const resetFieldSetToCache = (
    fieldSet: FieldSets.BusinessOwner | FieldSets.StoreLocation
  ) => {
    if (cachedFieldSetValues) {
      if (fieldSet instanceof FieldSets.BusinessOwner) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        merchantModel.owners[
          fieldSet.ownerIndex
        ] = FieldSets.BusinessOwner.fromJson(
          fieldSet,
          cachedFieldSetValues as FieldSets.BusinessOwnerValueMap
        );
      }
      if (fieldSet instanceof FieldSets.StoreLocation) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        merchantModel.locations[
          fieldSet.locationIndex
        ] = FieldSets.StoreLocation.fromJson(
          fieldSet,
          cachedFieldSetValues as FieldSets.StoreLocationValueMap
        );
      }
      syncStoreWithMerchant();
    }
  };

  const syncStoreWithMerchant = (): void => {
    dispatch(merchantActions.setMerchantData(merchantModel.toFieldValueMap()));
  };

  const updatePhoneCountryValidationProperty = (
    field: PhoneField,
    value: string
  ) => (field.phoneCountryNameForValidation = value);

  const getFieldSetUpdater = (fieldSet: FieldSet<any>) => (
    field: Field<any>,
    value: ValueOf<typeof field>
  ) => {
    if (fieldSet instanceof FieldSets.BusinessOwner) {
      merchantModel.owners[fieldSet.ownerIndex].fieldMap[
        field.propertyName
      ].value = value;
    } else if (fieldSet instanceof FieldSets.StoreLocation) {
      merchantModel.locations[fieldSet.locationIndex].fieldMap[
        field.propertyName
      ].value = value;
    } else if (fieldSet instanceof FieldSets.LeadFieldSet) {
      fieldSet.fieldMap[field.propertyName].value = value;
      /**
       * MLS does _not_ have the concept of multiple "fieldsets". Instead,
       *   they have concrete models that act as the store of record.
       *
       * We do not have that idea - instead we have fields that can show up
       *   in multiple fieldsets. It's a business rule in MOP UI that says e.g.
       *   "the first / last name from lead form should synced with first owner"
       *
       * As a result, this method is called to sync values from Lead Fieldset to
       *   the rest of MOPs FieldSets.
       */
      copyFromLeadform(merchantModel);
    } else {
      fieldSet.fieldMap[field.propertyName].value = value;
    }

    dispatch(
      merchantActions.setMerchantData(Merchant.getInstance().toFieldValueMap())
    );
  };

  const setPrimaryBankVerificationSource = (source: BankVerificationSource) =>
    (merchantModel.primaryBank.verificationSource = source);

  return {
    addFieldSetToCollection: (collectionType: keyof MerchantCollections) => {
      if (collectionType === "owners") {
        merchantModel.addAdditionalBusinessOwner();
      } else if (collectionType === "locations") {
        merchantModel.addAdditionalStoreLocation();
      }
      dispatch(
        merchantActions.setMerchantData(merchantModel.toFieldValueMap())
      );
    },
    errors: fieldErrorMap,
    formatMerchantForBankInfo: ({
      verificationSource,
      fieldSetName,
      setBankFormComplete,
    }: {
      verificationSource: BankVerificationSource;
      fieldSetName: BankFieldSetNames;
      setBankFormComplete: boolean;
    }) => {
      const primaryBankVerificationSource =
        merchantModel.primaryBank.verificationSource;
      const bankVerificationSource: BankVerificationSource =
        fieldSetName === "PrimaryBankFieldSet"
          ? primaryBankVerificationSource
          : verificationSource;
      const bankLabel =
        fieldSetName === "PrimaryBankFieldSet" ? "PRIMARY" : "SECONDARY";
      return merchantTransforms.transformForBankInfo(
        merchantModel,
        bankLabel,
        setBankFormComplete,
        bankVerificationSource
      );
    },
    formatMerchantForCustomerSupportInfo: () =>
      merchantTransforms.transformForCustomerSupportInfo(merchantModel),
    formatMerchantForSave: () =>
      merchantTransforms.transformForSave(merchantModel, storeLocationsFile),
    formatMerchantForSignup: () => {
      if (conditionallyUpdateSalesChannel(merchantModel)) {
        syncStoreWithMerchant();
      }
      return merchantTransforms.transformForSignup(email, merchantModel);
    },
    formatMerchantForStoreInfo: () => {
      const result = merchantTransforms.transformForUpdateStoreInfo(
        merchantModel,
        storeLocationsFile
      );
      return result;
    },
    formatMerchantForCashPayInfo: () =>
      merchantTransforms.transformForCashPayInfo(merchantModel),
    formatMerchantForContactUsInfo: (
      contentBody: string,
      merchantEmail: string,
      file?: File
    ) =>
      merchantTransforms.transformForContactUsInfo(
        merchantModel,
        merchantEmail,
        contentBody,
        file
      ),
    updatePhoneCountryValidationProperty,
    getFieldSetUpdater,
    merchant: merchantModel,
    removeFieldSetFromCollection: (
      collectionType: keyof MerchantCollections,
      index?: number
    ) => {
      if (collectionType === "owners") {
        merchantModel.removeBusinessOwner(index);
      } else if (collectionType === "locations") {
        merchantModel.removeStoreLocation(index);
      }
      dispatch(
        merchantActions.setMerchantData(merchantModel.toFieldValueMap())
      );
    },
    resetFieldSetToCache,
    setCachedFieldSetValues: (values) => setCachedFieldSetValues(values),
    setMerchant: (data: MerchantFieldSetValueMap | RestoreResponse) => {
      if (isRestore(data)) {
        dispatch(merchantActions.setMerchantAccount(data));
        /**
         * Will be undefined until user calls api/save
         */
        if (data.restore?.storeLocationsFile) {
          dispatch(
            merchantActions.setStoreLocationUpload(
              data.restore.storeLocationsFile
            )
          );
        }
      }
      dispatch(merchantActions.setMerchantData(data));
    },
    setMerchantLocationsFromJson: (
      locations: MerchantFieldSetValueMap["locations"]
    ) => {
      dispatch(
        merchantActions.setMerchantData({
          ...fieldValueMap,
          locations: locations.map((location) => ({
            ...location,
            label: uuidv4(),
            isMall: location.isMall ? "Yes" : "No",
            isResidence: location.isResidence ? "Yes" : "No",
          })),
        })
      );
    },
    setOnboardingLink: (link: string) => {
      dispatch(merchantActions.setOnboardingLink(link));
    },
    syncStoreWithMerchant,
    validateMerchant: (fieldSet?: FieldSet<any>): boolean => {
      let isValid = true;
      try {
        if (fieldSet) {
          fieldSet.validate({ stringify: true });
        } else {
          merchantModel.validateAll();
        }
      } catch (ex) {
        dispatch(errorActions.setFieldErrorMap(ex));
        isValid = false;
      }
      if (isValid) {
        // clear all errors for a given fieldset
        let index: number | undefined;
        if (fieldSet instanceof FieldSets.BusinessOwner) {
          index = fieldSet.ownerIndex;
        } else if (fieldSet instanceof FieldSets.StoreLocation) {
          index = fieldSet.locationIndex;
        }
        const fieldSetToClear = {
          index,
          isLocation: fieldSet instanceof FieldSets.StoreLocation,
          isOwner: fieldSet instanceof FieldSets.BusinessOwner,
          name: fieldSet?.fieldSetName,
        };
        dispatch(errorActions.clearFieldErrorMap(fieldSetToClear));
      }
      return isValid;
    },
    validateCustomFields: (
      fieldPropertyListToValidate: string[],
      fieldSet: FieldSets.LeadFieldSet
    ) => {
      let isValid = true;
      try {
        fieldSet.validate({ stringify: true, fieldPropertyListToValidate });
      } catch (ex) {
        dispatch(errorActions.setFieldErrorMap(ex));
        isValid = false;
      }

      if (isValid) {
        dispatch(
          errorActions.clearFieldErrorMap({ isLocation: false, isOwner: false })
        );
      }
      return isValid;
    },
    primaryBankVerificationSource: merchantModel.primaryBank.verificationSource,
    setPrimaryBankVerificationSource,
  };
}
